import * as React from "react"
import { useDropzone, FileRejection } from "react-dropzone"
import { Upload, X, File as FileIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "./button"
import { Card, CardContent } from "./card"
import { Progress } from "./progress"
import { Badge } from "./badge"

export interface FileWithProgress {
  file: File
  progress?: number
  status?: "pending" | "uploading" | "completed" | "error"
  error?: string
}

/** Stable default — inline `[]` in param defaults is a new reference each render and breaks sync effects. */
const EMPTY_FILE_LIST: FileWithProgress[] = []

export interface FileUploadProps {
  /** Accepted file types (MIME types or extensions) */
  accept?: Record<string, string[]>
  /** Maximum file size in bytes */
  maxSize?: number
  /** Maximum number of files */
  maxFiles?: number
  /** Whether to allow multiple files */
  multiple?: boolean
  /** Callback when files are selected */
  onFilesSelected?: (files: File[]) => void
  /** Callback when files are removed */
  onFileRemove?: (file: File) => void
  /** Files with progress tracking */
  files?: FileWithProgress[]
  /** Disabled state */
  disabled?: boolean
  /** Custom className */
  className?: string
  /**
   * When false, the selected-file list below the dropzone is hidden. Use for image flows
   * where the parent shows a custom preview.
   * @default true
   */
  showSelectedList?: boolean
  /**
   * When true and at least one file is selected, hide the dropzone (file list + remove remains).
   * Useful for single-file flows so the empty uploader does not stay visible.
   * @default false
   */
  hideDropzoneWhenHasFiles?: boolean
  /** Main dropzone copy. */
  title?: string
  /** Dropzone copy while files are dragged over it. */
  activeTitle?: string
  /** Helper text below the main dropzone copy. */
  description?: string
  /** Whether to render the explicit select button below the dropzone copy. */
  showSelectButton?: boolean
  /** Custom className for the outer dropzone card. */
  dropzoneCardClassName?: string
  /** Shorter dropzone (e.g. next to a preview) without changing behaviour. */
  dropzoneClassName?: string
  /**
   * When set, assigns `current` to a function that opens the file picker (same as the dropzone).
   * Use for an external control (e.g. header + button).
   */
  dropzoneOpenRef?: React.MutableRefObject<(() => void) | null>
}

export function FileUpload({
  accept,
  maxSize = 10 * 1024 * 1024, // 10MB default
  maxFiles,
  multiple = true,
  onFilesSelected,
  onFileRemove,
  files = EMPTY_FILE_LIST,
  disabled = false,
  className,
  showSelectedList = true,
  hideDropzoneWhenHasFiles = false,
  title,
  activeTitle = "Drop files here",
  description,
  showSelectButton = true,
  dropzoneCardClassName,
  dropzoneClassName,
  dropzoneOpenRef,
}: FileUploadProps) {
  const [fileList, setFileList] = React.useState<FileWithProgress[]>(files)

  React.useEffect(() => {
    setFileList(files)
  }, [files])

  const showDropzone = !(hideDropzoneWhenHasFiles && fileList.length > 0)

  const onDrop = React.useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles.length > 0) {
        // Handle rejected files
        console.warn("Some files were rejected:", rejectedFiles)
        return
      }

      const newFiles: FileWithProgress[] = acceptedFiles.map((file) => ({
        file,
        progress: 0,
        status: "pending",
      }))

      const updatedFiles = multiple
        ? [...fileList, ...newFiles].slice(0, maxFiles || Infinity)
        : newFiles.slice(0, 1)

      setFileList(updatedFiles)
      onFilesSelected?.(acceptedFiles)
    },
    [fileList, multiple, maxFiles, onFilesSelected]
  )

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept,
    maxSize,
    maxFiles: maxFiles || (multiple ? undefined : 1),
    multiple,
    disabled,
    noClick: false,
    noKeyboard: false,
  })

  React.useEffect(() => {
    if (!dropzoneOpenRef) return
    dropzoneOpenRef.current = open
    return () => {
      dropzoneOpenRef.current = null
    }
  }, [open, dropzoneOpenRef])

  const handleRemove = (fileToRemove: File) => {
    const updated = fileList.filter((f) => f.file !== fileToRemove)
    setFileList(updated)
    onFileRemove?.(fileToRemove)
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i]
  }

  return (
    <div className={cn("space-y-4", className)}>
      {showDropzone ? (
        <Card
          {...getRootProps()}
          className={cn(
            "cursor-pointer transition-colors",
            isDragActive && "border-primary bg-primary/5",
            disabled && "cursor-not-allowed opacity-50",
            dropzoneCardClassName
          )}
        >
          <CardContent
            className={cn(
              "flex flex-col items-center justify-center gap-4 p-8",
              dropzoneClassName
            )}
          >
            <input {...getInputProps()} />
            <div
              className={cn(
                "flex size-12 items-center justify-center rounded-full border-2 border-dashed",
                isDragActive ? "border-primary bg-primary/10" : "border-muted-foreground/25"
              )}
            >
              <Upload
                className={cn(
                  "size-6",
                  isDragActive ? "text-primary" : "text-muted-foreground"
                )}
              />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium">
                {isDragActive
                  ? activeTitle
                  : title ?? "Drag and drop files here, or click to select"}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {description ?? (
                  <>
                    {accept
                      ? `Accepted: ${Object.values(accept).flat().join(", ")}`
                      : "Any file type"}
                    {maxSize && ` • Max size: ${formatFileSize(maxSize)}`}
                    {maxFiles && ` • Max files: ${maxFiles}`}
                  </>
                )}
              </p>
            </div>
            {showSelectButton && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  open()
                }}
                disabled={disabled}
              >
                Select files
              </Button>
            )}
          </CardContent>
        </Card>
      ) : null}

      {showSelectedList && fileList.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">
            {fileList.length === 1 ? "Selected file" : `Selected Files (${fileList.length})`}
          </h4>
          <div className="space-y-2">
            {fileList.map((fileWithProgress, index) => (
              <Card key={index}>
                <div className="flex items-center gap-3">
                  <FileIcon className="size-5 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium truncate">
                        {fileWithProgress.file.name}
                      </p>
                      <div className="flex items-center gap-2 shrink-0">
                        {fileWithProgress.status && (
                          <Badge
                            variant={
                              fileWithProgress.status === "completed"
                                ? "default"
                                : fileWithProgress.status === "error"
                                ? "destructive"
                                : "secondary"
                            }
                          >
                            {fileWithProgress.status}
                          </Badge>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-6"
                          onClick={() => handleRemove(fileWithProgress.file)}
                          disabled={disabled}
                        >
                          <X className="size-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>{formatFileSize(fileWithProgress.file.size)}</span>
                        {fileWithProgress.progress !== undefined &&
                          fileWithProgress.progress > 0 && (
                            <span>{fileWithProgress.progress}%</span>
                          )}
                      </div>
                      {fileWithProgress.progress !== undefined &&
                        fileWithProgress.progress > 0 &&
                        fileWithProgress.status === "uploading" && (
                          <Progress value={fileWithProgress.progress} />
                        )}
                      {fileWithProgress.error && (
                        <p className="text-xs text-destructive">{fileWithProgress.error}</p>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
